import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { ControlValueAccessor, FormBuilder, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validator, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { CrudService, Patient, PatientAccountModel, PatientInsurance } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, filter, Subject, switchMap, takeUntil } from 'rxjs'

@Component({
  selector: 'mmx-patient-account-picker',
  templateUrl: './patient-account-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    FlexModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PatientAccountPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PatientAccountPickerComponent),
      multi: true,
    },
  ],
})
export class PatientAccountPickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  @Input()
  public patient: Patient

  @Input()
  public required = false

  private changes$ = new BehaviorSubject<SimpleChanges>(null)
  private onDestroy$ = new Subject<void>()
  private onChange: (value: Partial<{ eid: string, insurances?: PatientInsurance[] }>) => void
  private onTouched: () => void

  public isCreateMode$ = new BehaviorSubject<boolean>(false)
  public formControl = new FormControl<PatientAccountModel>(null)

  public patientAccounts$ = this.changes$
    .pipe(
      filter(() => !!this.patient?.id),
      switchMap(() => this.crudService.index(PatientAccountModel, { patientId: this.patient.id })),
    )

  public insurances$ = this.changes$
    .pipe(
      filter(() => !!this.patient?.id),
      switchMap(() => this.crudService.index(PatientInsurance, { patientId: this.patient.id })),
    )

  public accountForm = this.fb.group({
    eid: this.fb.control<string>(null, Validators.required),
    insurances: this.fb.control<PatientInsurance[]>([], Validators.required),
  })

  constructor(
    private crudService: CrudService,
    private fb: FormBuilder,
  ) { }

  public ngOnInit() {
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => {
        this.isCreateMode$.next(false)
        this.onChange?.(value)
        this.onTouched?.()
      })

    this.accountForm.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.onChange?.(this.accountForm.value)
        this.onTouched?.()
      })
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.changes$.next(changes)
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(account: PatientAccountModel | null): void {
    this.formControl.setValue(account)
  }

  public registerOnChange(fn: (value: PatientAccountModel) => void): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public createNewPatientAccount() {
    this.isCreateMode$.next(true)
    this.onTouched?.()
  }

  public validate() {
    if (this.isCreateMode$.value) {
      return this.accountForm.valid ? null : { invalid: true }
    }

    return null
  }

  public compareAccounts(a1: PatientAccountModel, a2: PatientAccountModel) {
    return a1?.id === a2?.id
  }
}
